import { Section } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  isEmployee,
  isEmploymentOther,
  isExistingCommercial,
  isHomeowner,
  isRenting,
  isRetired,
  isSelfEmployed,
} from 'app/models/fields/conditionals';
import { ConfirmationCommercial } from 'app/components/ConfirmationCommercial';
import { getCommercialDeclarationQuestions } from '../questions/declarations';
import {
  COMMERCIAL_BRIDGE_LOAN_BORROWER_EMPLOYMENT,
  COMMERCIAL_BRIDGE_LOAN_BORROWER_HOME_INFO,
  COMMERCIAL_BRIDGE_LOAN_BORROWER_INCOME_SOURCE,
  COMMERCIAL_BRIDGE_LOAN_BORROWER_LIVING_SITUATION,
  COMMERCIAL_BRIDGE_LOAN_BORROWER_QUESTIONS,
  COMMERCIAL_BRIDGE_LOAN_BORROWER_RENTER_INFO,
  COMMERCIAL_BRIDGE_LOAN_BORROWER_RETIREMENT,
  COMMERCIAL_BRIDGE_LOAN_BUSINESS_FINANCIALS,
  COMMERCIAL_BRIDGE_LOAN_BUSINESS_INFO,
  COMMERCIAL_BRIDGE_LOAN_BUSINESS_MANAGEMENT,
  COMMERCIAL_BRIDGE_LOAN_BUSINESS_OWNERS,
  COMMERCIAL_BRIDGE_LOAN_EXISTING_CUSTOMER,
  COMMERCIAL_BRIDGE_LOAN_EXISTING_CUSTOMER_INFO,
  COMMERCIAL_BRIDGE_LOAN_FINAL,
  COMMERCIAL_BRIDGE_LOAN_HOLDER,
  COMMERCIAL_BRIDGE_LOAN_LOAN_INFO,
  COMMERCIAL_BRIDGE_LOAN_PERSONAL_INFO,
  COMMERCIAL_BRIDGE_LOAN_TYPE,
} from 'app/models/sections/constants';
import { getCommercialBridgeLoanQuestionsByName } from '../questions/commercial-bridge-loan';
import { FormTerms } from '../../components/FormAgreements/FormTerms';
import React from 'react';
import { handleCommercialBackButton, handleCommercialFormSubmit } from '../../actions/form/actions';
import { BorrowerType } from '@lenderful/domain';
import { ajaxValidateBorrowerResidence } from 'app/actions/form/address-validation/actions';
import { Disclosure } from 'app/components/Disclaimer/Disclosure';

export const getCommercialBridgeLoanSectionById = (id: number): Section => {
  return COMMERCIAL_BRIDGE_LOAN_SECTIONS.find((section) => section.id === id) as Section;
};

const COMMERCIAL_BRIDGE_LOAN_SECTIONS: Section[] = [
  {
    id        : COMMERCIAL_BRIDGE_LOAN_TYPE,
    questions : getCommercialBridgeLoanQuestionsByName([
      FieldNames.commercialBridgeOrCredit,
    ]),
    agreement: <FormTerms />,
    hideBackButton: handleCommercialBackButton,
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_LOAN_INFO,
    title     : 'Tell us more about your loan needs',
    questions : getCommercialBridgeLoanQuestionsByName([
      FieldNames.loanAmount,
      FieldNames.commercialLoanPurpose,
      FieldNames.commercialLoanTiming,
    ]),
  },
  {
    id             : COMMERCIAL_BRIDGE_LOAN_EXISTING_CUSTOMER,
    questions      : getCommercialBridgeLoanQuestionsByName([
      FieldNames.isCommercialExistingCustomer,
    ]),
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_BUSINESS_INFO,
    title     : 'Tell us about your business',
    questions : getCommercialBridgeLoanQuestionsByName([
      FieldNames.commercialBusinessName,
      FieldNames.commercialDBA,
      FieldNames.commercialCensusTract,
      FieldNames.commercialNAICSCode,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
      FieldNames.commercialPhone,
      FieldNames.commercialEmployeeCount,
      FieldNames.commercialWebsite,
      FieldNames.commercialEntityType,
      FieldNames.commercialBusinessType,
      FieldNames.commercialBusinessTypeOther,
      FieldNames.commercialStateOfIncorporation,
      FieldNames.commercialYearStarted,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_BUSINESS_FINANCIALS,
    title     : 'Tell us about your business financials',
    questions : getCommercialBridgeLoanQuestionsByName([
      FieldNames.commercialRevenueTrend3,
      FieldNames.commercialRevenueLastYear,
      FieldNames.commercialRevenueExplanation,
      FieldNames.commercialProfitTrend3,
      FieldNames.commercialProfitExplanation,
      FieldNames.commercialCollateralList,
      FieldNames.commercialBusinessOwnershipType,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_BUSINESS_OWNERS,
    questions : getCommercialBridgeLoanQuestionsByName([
      FieldNames.commercialOwnerList,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_BUSINESS_MANAGEMENT,
    questions : getCommercialBridgeLoanQuestionsByName([
      FieldNames.hasOtherManagement,
      FieldNames.commercialManagementList,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_HOLDER,
    questions : getCommercialBridgeLoanQuestionsByName([
      FieldNames.commercialLoanHolder,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id       : COMMERCIAL_BRIDGE_LOAN_PERSONAL_INFO ,
    title    : 'Enter your personal information',
    questions: getCommercialBridgeLoanQuestionsByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.email,
      FieldNames.phone,
      FieldNames.dateOfBirth,
    ]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_BORROWER_LIVING_SITUATION,
    title     : 'What is your current housing situation?',
    questions : getCommercialBridgeLoanQuestionsByName([FieldNames.livingRentOrOwn]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id       : COMMERCIAL_BRIDGE_LOAN_BORROWER_HOME_INFO,
    title    : 'Provide your homeowner information',
    questions: getCommercialBridgeLoanQuestionsByName([
      FieldNames.livingPropertyType,
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
    ]),
    showIf: [(values) => isHomeowner(values) && !isExistingCommercial(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_BORROWER_RENTER_INFO,
    title     : 'Provide your renter information',
    questions : getCommercialBridgeLoanQuestionsByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingMonthlyRentAmount,
    ]),
    showIf : [(values) => isRenting(values) && !isExistingCommercial(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_BORROWER_INCOME_SOURCE,
    title     : 'What is your income source?',
    questions : getCommercialBridgeLoanQuestionsByName([FieldNames.employmentType]),
    showIf: [(values) => !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_BORROWER_EMPLOYMENT,
    title     : 'Tell us about your income and employment',
    questions : getCommercialBridgeLoanQuestionsByName([
      FieldNames.commercialEmploySameBusiness,
      FieldNames.employCompanyName,
      FieldNames.employStreet,
      FieldNames.employStreet2,
      FieldNames.employCity,
      FieldNames.employState,
      FieldNames.employZip,
      FieldNames.employTitle,
      FieldNames.employPhone,
      FieldNames.employStart,
      FieldNames.employBase,
      FieldNames.employOvertime,
      FieldNames.employCommission,
      FieldNames.employBonus,
      FieldNames.additionalIncome,
      FieldNames.employPrevName,
      FieldNames.employPrevStreet,
      FieldNames.employPrevStreet2,
      FieldNames.employPrevCity,
      FieldNames.employPrevState,
      FieldNames.employPrevZip,
      FieldNames.employPrevTitle,
      FieldNames.employPrevStart,
      FieldNames.employPrevEnd,
    ]),
    showIf       : [(values) => (isEmployee(values) || isSelfEmployed(values)) && !isExistingCommercial(values)],
  },
  {
    id        : COMMERCIAL_BRIDGE_LOAN_BORROWER_RETIREMENT,
    questions : getCommercialBridgeLoanQuestionsByName([
      FieldNames.retirementIncome,
      FieldNames.otherIncome,
    ]),
    showIf       : [(values) => (isRetired(values) ||  isEmploymentOther(values)) && !isExistingCommercial(values)],
  },
  {
    id           : COMMERCIAL_BRIDGE_LOAN_EXISTING_CUSTOMER_INFO,
    title        : 'Please enter your Loan and Business Information',
    questions    : getCommercialBridgeLoanQuestionsByName([
      FieldNames.existingLoanNumber,
      FieldNames.commercialBusinessName,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
      FieldNames.firstName,
      FieldNames.lastName,
      FieldNames.email,
      FieldNames.phone,
    ]),
    showIf       : [isExistingCommercial],
    submitAction : handleCommercialFormSubmit,
    submitText   : 'Submit',
  },
  {
    id           : COMMERCIAL_BRIDGE_LOAN_BORROWER_QUESTIONS,
    title        : 'Borrower Questions',
    questions    : getCommercialDeclarationQuestions(BorrowerType.PRIMARY),
    showIf: [(values) => !isExistingCommercial(values)],
    submitAction : handleCommercialFormSubmit,
    submitText   : 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id              : COMMERCIAL_BRIDGE_LOAN_FINAL,
    ifNoQuestions   : ConfirmationCommercial,
    hideProgressBar : true,
  },
];
